import type { AgentsDemoOrg, OrgAgent, OrgConnection, OrgSchedule } from "@/lib/agentsDemoData";

export type AgentsViewerMode = "super_admin" | "standard";

const STORAGE_VERSION = 4;
const STORAGE_KEY = "atw_agents_demo_v1";

// Mailbox fields live on each agent object, and the mail flag lives on each
// connection object. v4 already stores those arrays whole, so older snapshots
// keep hired agents and saved connections (including empty arrays). Missing
// mailbox fields mean null.
// Schedules are a new org list on the same v4 snapshot. A missing list means
// none yet. An empty schedules array stays empty. Assignments live on each
// agent as scheduleIds. Do not bump the version for them.

export type AgentsPersistedSnapshot = {
  v: number;
  agents: OrgAgent[];
  maxAddon: boolean;
  connections: OrgConnection[];
  schedules: OrgSchedule[];
  viewerMode: AgentsViewerMode;
};

function isViewerMode(v: unknown): v is AgentsViewerMode {
  return v === "super_admin" || v === "standard";
}

function isAgentList(value: unknown): value is OrgAgent[] {
  return Array.isArray(value);
}

function readSchedules(value: unknown): OrgSchedule[] {
  return Array.isArray(value) ? (value as OrgSchedule[]) : [];
}

function safeParse(raw: string | null): AgentsPersistedSnapshot | null {
  if (!raw) return null;
  try {
    const j = JSON.parse(raw) as {
      v?: unknown;
      agents?: unknown;
      seats?: unknown;
      maxAddon?: unknown;
      connections?: unknown;
      schedules?: unknown;
      viewerMode?: unknown;
    };
    if (!isViewerMode(j.viewerMode)) return null;
    if (typeof j.maxAddon !== "boolean") return null;
    if (typeof j.v !== "number") return null;

    // v4 keeps hired agents and any connections added in this browser.
    // An empty connections array is a real value. Do not treat it as missing.
    // v1-v3 saved the sample connections. Drop those so they do not come back.
    // Keep the saved agents list so hired agents stay.
    // Schedules are optional on older v4 snapshots. Missing means none.
    // An empty schedules array stays empty. Never replace it with samples.
    let agents: OrgAgent[] | null = null;
    let connections: OrgConnection[] | null = null;
    let schedules: OrgSchedule[] = [];
    if (j.v === STORAGE_VERSION) {
      if (!isAgentList(j.agents) || !Array.isArray(j.connections)) return null;
      agents = j.agents;
      connections = j.connections as OrgConnection[];
      schedules = readSchedules(j.schedules);
    } else if (j.v === 1 || j.v === 2 || j.v === 3) {
      agents = isAgentList(j.agents) ? j.agents : [];
      connections = [];
      schedules = [];
    }
    if (agents === null || connections === null) return null;

    return {
      v: STORAGE_VERSION,
      agents,
      maxAddon: j.maxAddon,
      connections,
      schedules,
      viewerMode: j.viewerMode,
    };
  } catch {
    return null;
  }
}

export function loadAgentsPersistedSnapshot(): AgentsPersistedSnapshot | null {
  if (typeof window === "undefined") return null;
  return safeParse(window.localStorage.getItem(STORAGE_KEY));
}

export function mergeOrgWithPersisted(
  base: AgentsDemoOrg,
  snap: AgentsPersistedSnapshot | null,
): AgentsDemoOrg {
  if (!snap) return base;
  return {
    ...base,
    agents: snap.agents,
    maxAddon: snap.maxAddon,
    connections: snap.connections,
    schedules: snap.schedules,
  };
}

export function buildPersistedSnapshot(
  org: AgentsDemoOrg,
  viewerMode: AgentsViewerMode,
): AgentsPersistedSnapshot {
  return {
    v: STORAGE_VERSION,
    agents: org.agents,
    maxAddon: org.maxAddon,
    connections: org.connections,
    schedules: org.schedules,
    viewerMode,
  };
}

export function saveAgentsPersistedSnapshot(snap: AgentsPersistedSnapshot): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(snap));
  } catch {
    /* quota or private mode */
  }
}

export function clearAgentsPersistedSnapshot(): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}
